import type { Theme } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ThumbUpOffAlt from "@mui/icons-material/ThumbUpOffAlt";
import HelpOutline from "@mui/icons-material/HelpOutline";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import MoreHorizOutlined from "@mui/icons-material/MoreHorizOutlined";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  WorkaroundIcon,
  FalsePositiveIcon,
  DisabledIcon,
  StatusWarnIcon,
  StatusPositiveIcon,
  StatusProg0Icon,
  StatusProg25Icon,
  StatusProg50Icon,
  StatusProg75Icon,
  StatusDoneIcon,
  StatusNaIcon,
  StatusNegativeIcon,
} from "~/icons";
import {
  FindingActionState,
  FindingExceptionState,
  FindingOverallState,
  FindingTicketState,
  type FindingState,
} from "~/operations";
import type { FindingStatusConfig, MenuCallbacks, MenuItemDef } from "./types";

// TODO: update colors when new variables and colors are introduced
const inverseCanvas = (theme: Theme): string =>
  theme.palette.mode === "light" ? "#292928" : "#F0F0F0";

const inverseText = (theme: Theme): string =>
  theme.palette.mode === "light" ? "#FFFFFF" : "#292928";

const statusConfigMap: Partial<Record<string, FindingStatusConfig>> = {
  NEW: {
    label: "New",
    icon: StatusWarnIcon,
    backgroundColor: (theme) => theme.palette.critical.main,
    color: () => "#FFFFFF",
  },
  REGRESSED: {
    label: "Regressed",
    icon: StatusWarnIcon,
    backgroundColor: (theme) => theme.palette.critical.main,
    color: () => "#FFFFFF",
  },
  DETECTED: {
    label: "Detected",
    icon: StatusPositiveIcon,
    backgroundColor: (theme) => theme.palette.action.hover,
    color: (theme) => theme.palette.text.primary,
  },
  FIX_AVAILABLE: {
    label: "Fix available",
    icon: StatusPositiveIcon,
    backgroundColor: (theme) => theme.palette.action.hover,
    color: (theme) => theme.palette.text.primary,
  },
  TICKETED: {
    label: "Ticketed",
    icon: StatusProg0Icon,
    backgroundColor: inverseCanvas,
    color: inverseText,
  },
  IN_PROGRESS: {
    label: "In progress",
    icon: StatusProg50Icon,
    backgroundColor: inverseCanvas,
    color: inverseText,
  },
  PR_CREATED: {
    label: "PR created",
    icon: StatusProg25Icon,
    backgroundColor: inverseCanvas,
    color: inverseText,
  },
  APPROVED: {
    label: "Approved",
    icon: CheckCircle,
    backgroundColor: inverseCanvas,
    color: inverseText,
  },
  DEPLOYED: {
    label: "Deployed",
    icon: StatusProg75Icon,
    backgroundColor: inverseCanvas,
    color: inverseText,
  },
  WORKAROUND: {
    label: "Workaround",
    icon: WorkaroundIcon,
    backgroundColor: (theme) => theme.palette.action.hover,
    color: (theme) => theme.palette.text.secondary,
  },
  FALSE_POSITIVE: {
    label: "False positive",
    icon: FalsePositiveIcon,
    backgroundColor: (theme) => theme.palette.action.hover,
    color: (theme) => theme.palette.text.secondary,
  },
  RISK_ACCEPTED: {
    label: "Risk accepted",
    icon: ThumbUpOffAlt,
    backgroundColor: (theme) => theme.palette.action.hover,
    color: (theme) => theme.palette.text.secondary,
  },
  DISABLED: {
    label: "Disabled",
    icon: DisabledIcon,
    backgroundColor: (theme) => theme.palette.action.hover,
    color: (theme) => theme.palette.text.secondary,
  },
  FIXED: {
    label: "Fixed",
    icon: StatusDoneIcon,
    backgroundColor: (theme) => theme.palette.none.main,
    color: () => "#FFFFFF",
  },
  UNAFFECTED: {
    label: "Unaffected",
    icon: StatusNaIcon,
    backgroundColor: (theme) => theme.palette.action.hover,
    color: (theme) => theme.palette.text.primary,
  },
  ERROR: {
    label: "Error",
    icon: WarningAmber,
    backgroundColor: (theme) => theme.palette.errorScan.main,
    color: (theme) => theme.palette.errorScan.contrastText,
  },
};

const fallbackConfig: FindingStatusConfig = {
  label: "Unknown",
  icon: HelpOutline,
  backgroundColor: (theme) => theme.palette.action.hover,
  color: (theme) => theme.palette.text.secondary,
};

export function getStatusConfig(state: string): FindingStatusConfig {
  return statusConfigMap[state] ?? fallbackConfig;
}

const EXCEPTION_STATES: Set<string> = new Set([
  FindingOverallState.Workaround,
  FindingOverallState.FalsePositive,
  FindingOverallState.RiskAccepted,
  FindingOverallState.Disabled,
]);

const ACTIVE_FIX_STATES: Set<string> = new Set([
  FindingOverallState.New,
  FindingOverallState.Regressed,
  FindingOverallState.Detected,
]);

const SCRIPT_ACTION_STATES = new Set([
  FindingActionState.Script,
  FindingActionState.SecurityPipeline,
]);

function getTicketItem(
  states: FindingState,
  callbacks: MenuCallbacks,
): MenuItemDef {
  if (states.ticket === FindingTicketState.Ticketed) {
    return {
      key: "view-ticket",
      label: "View ticket",
      icon: StatusProg50Icon,
      onClick: callbacks.onViewTicket,
    };
  }
  return {
    key: "create-ticket",
    label: "Create ticket",
    icon: StatusProg0Icon,
    disabled: callbacks.canCreateTicket === false,
    onClick: callbacks.onCreateTicket,
  };
}

function getExceptionItem(
  states: FindingState,
  callbacks: MenuCallbacks,
): MenuItemDef {
  if (states.exception !== FindingExceptionState.None) {
    return {
      key: "view-exception",
      label: "View exception",
      icon: StatusNegativeIcon,
      onClick: callbacks.onViewException,
    };
  }
  return {
    key: "set-exception",
    label: "Set exception",
    icon: StatusNegativeIcon,
    disabled: callbacks.canSetException === false,
    onClick: callbacks.onSetException,
  };
}

export function getMenuItems(
  states: FindingState,
  callbacks: MenuCallbacks,
): MenuItemDef[] {
  const { overall: overallState, action: actionState } = states;

  if (EXCEPTION_STATES.has(overallState)) {
    return [
      {
        key: "view-exception",
        label: "View exception",
        icon: StatusNegativeIcon,
        onClick: callbacks.onViewException,
      },
      {
        key: "copy-link",
        label: "Copy link",
        icon: LinkOutlined,
        dividerBefore: true,
        onClick: callbacks.onCopyLink,
      },
      {
        key: "view-details",
        label: "View details",
        icon: MoreHorizOutlined,
        onClick: callbacks.onViewDetails,
      },
    ];
  }

  if (overallState === FindingOverallState.Fixed) {
    return [
      {
        key: "view-fix",
        label: "View fix",
        icon: StatusDoneIcon,
        onClick: callbacks.onViewFix,
      },
      {
        key: "copy-link",
        label: "Copy link",
        icon: LinkOutlined,
        dividerBefore: true,
        onClick: callbacks.onCopyLink,
      },
      {
        key: "view-details",
        label: "View details",
        icon: MoreHorizOutlined,
        onClick: callbacks.onViewDetails,
      },
    ];
  }

  if (
    overallState === FindingOverallState.Ticketed ||
    overallState === FindingOverallState.InProgress
  ) {
    return [
      {
        key: "fix-in-progress",
        label: "Fix in progress",
        icon: overallState === "TICKETED" ? StatusProg0Icon : StatusProg50Icon,
        disabled: true,
      },
      {
        key: "view-ticket",
        label: "View ticket",
        icon: StatusProg50Icon,
        onClick: callbacks.onViewTicket,
      },
      getExceptionItem(states, callbacks),
      {
        key: "copy-link",
        label: "Copy link",
        icon: LinkOutlined,
        dividerBefore: true,
        onClick: callbacks.onCopyLink,
      },
      {
        key: "view-details",
        label: "View details",
        icon: MoreHorizOutlined,
        onClick: callbacks.onViewDetails,
      },
    ];
  }

  if (overallState === FindingOverallState.FixAvailable) {
    return [
      {
        key: "fix-finding",
        label: "Fix finding",
        icon: StatusDoneIcon,
        onClick: callbacks.onFixFinding,
      },
      getTicketItem(states, callbacks),
      getExceptionItem(states, callbacks),
      {
        key: "copy-link",
        label: "Copy link",
        icon: LinkOutlined,
        dividerBefore: true,
        onClick: callbacks.onCopyLink,
      },
      {
        key: "view-details",
        label: "View details",
        icon: MoreHorizOutlined,
        onClick: callbacks.onViewDetails,
      },
    ];
  }

  if (ACTIVE_FIX_STATES.has(overallState)) {
    if (SCRIPT_ACTION_STATES.has(actionState)) {
      return [
        {
          key: "fix-finding",
          label: "Fix finding",
          icon: StatusDoneIcon,
          onClick: callbacks.onFixFinding,
        },
        getTicketItem(states, callbacks),
        getExceptionItem(states, callbacks),
        {
          key: "copy-link",
          label: "Copy link",
          icon: LinkOutlined,
          dividerBefore: true,
          onClick: callbacks.onCopyLink,
        },
        {
          key: "view-details",
          label: "View details",
          icon: MoreHorizOutlined,
          onClick: callbacks.onViewDetails,
        },
      ];
    }

    return [
      {
        key: "fix-unavailable",
        label: "Fix unavailable",
        icon: StatusDoneIcon,
        disabled: true,
      },
      getTicketItem(states, callbacks),
      getExceptionItem(states, callbacks),
      {
        key: "copy-link",
        label: "Copy link",
        icon: LinkOutlined,
        dividerBefore: true,
        onClick: callbacks.onCopyLink,
      },
      {
        key: "view-details",
        label: "View details",
        icon: MoreHorizOutlined,
        onClick: callbacks.onViewDetails,
      },
    ];
  }

  return [
    {
      key: "copy-link",
      label: "Copy link",
      icon: LinkOutlined,
      onClick: callbacks.onCopyLink,
    },
    {
      key: "view-details",
      label: "View details",
      icon: MoreHorizOutlined,
      onClick: callbacks.onViewDetails,
    },
  ];
}
